import type { PollutionReason } from "../protocol/events";
import type {
  AttachmentEffect,
  CardInstance,
  Composition,
  GridPos,
  NodeKind,
  PrecursorTier,
  RecipeDef,
  Rotation,
  ScriptedEventDef,
  LiveBuildRule,
  SetupOffer,
  SourcingDef,
} from "../protocol/snapshot";

/**
 * Authoring types the engine consumes. The *shapes* live here because the
 * engine's behaviour is defined by them; the *data* lives in @reagent/content,
 * zod-validated against these shapes. Inside `sim` itself, `engine/testFixture.ts`
 * and `mock/fixture.ts` supply the small hand-built sets the engine's own tests
 * and the replay CLI run against, so the engine never imports content.
 *
 * Everything is integer-only — quantities, heat, HP — so a run can never
 * drift through floating point.
 */

export type PipeMaterial = "glass" | "ptfeLined" | "hastelloy";

/**
 * `RecipeDef` and `AttachmentEffect` live in `protocol/snapshot.ts` now — a
 * stage's recipes and a node's fitted attachments are both part of
 * `GridSnapshot`, and `protocol/` must not import from `engine/`. Re-exported
 * from here so stage and card authors keep importing them from this module.
 */
export type {
  RecipeDef,
  AttachmentEffect,
  ScriptedEventDef,
  ScriptedEventKind,
  SetupOffer,
  PrecursorTier,
  SourcingDef,
  LiveBuildRule,
} from "../protocol/snapshot";

/**
 * `metaUtility` is the fifth category (spec 0020, `02-cards-and-economy.md`
 * §2.2): independent cards bought in the Phase 1 shop that need no grid
 * hardware to exist in the deck. The owner recategorised two existing
 * directive cards into it (Overtime Shift, Rush Order, spec 0020 Open
 * Question 2) rather than shipping the category empty — their `directive`
 * payload is unchanged, so every switch on `card.category` that used to read
 * `"directive"` for them now needs a `"metaUtility"` arm alongside it.
 */
export type CardCategory = "hardware" | "metaUtility" | "directive" | "attachment" | "hazard";

/** What a directive does the instant it is played. */
export type DirectiveEffect =
  | { readonly kind: "flashFreeze" }
  /** Spec 0035: targets one incubator; residenceTicks := 0 on each buffered packet, holdTicks kept. */
  | { readonly kind: "stirVat" }
  /** Spec 0035: targets one reactor; its firings are capped at `maxMultiple` multiples until the next `draw`. */
  | { readonly kind: "lowFire"; readonly maxMultiple: number }
  | { readonly kind: "solventFlush" }
  | { readonly kind: "nitrogenPurge" }
  /** §2.5's named mitigation: dumps a node's buffer, sludge and all. */
  | { readonly kind: "emergencyFlush" }
  | { readonly kind: "patchWeld"; readonly hp: number }
  | { readonly kind: "catalystRecharge" }
  | { readonly kind: "overtime"; readonly extraKw: number }
  | { readonly kind: "rushOrder"; readonly cards: number }
  /** §3.3 (spec 0033): clears the infestation on the node at `at`. */
  | { readonly kind: "fumigate" }
  /** Spec 0067: clears the contamination on the tile at `at`, for kW instead of the ₵ `cleanTile` fee. */
  | { readonly kind: "scrubTile" };

/**
 * What a hazard card does while held. One member per row of `03-hazards.md`
 * §4 — see `HazardPenaltyKind` (protocol/events.ts), which this is keyed off
 * of so the design table and the engine type cannot drift apart (spec 0007).
 * Unlike the five members it replaced, most of these are NOT "damage done at
 * end of Play" — `engine/hazardCards.ts` reads them live from a card sitting
 * in hand; see that module and `.claude/rules/sim-engine.md` for exactly
 * which of the four callers applies which kind.
 */
export type HazardEffect =
  /** Thermal Shock: -1 kW while held, applied at Play entry. */
  | { readonly kind: "powerPenalty"; readonly kw: number }
  /** Line Leak: hand draw size -1 the turn after it was left unplayed. */
  | { readonly kind: "drawPenalty"; readonly cards: number }
  /** Corrosive Pitting: +1 kW to every Directive played the same turn, per copy held. */
  | { readonly kind: "directiveSurcharge"; readonly kw: number }
  /** Catalyst Deactivation: 2₵ maintenance fee per surviving copy, charged at every stage end. */
  | { readonly kind: "maintenanceFee"; readonly credits: number }
  /** Clogged Line: locks 1 active grid tile until played and cleared. */
  | { readonly kind: "tileLock" }
  /** Chemical Hazard Spill: locks 1 hand slot until played and cleared. */
  | { readonly kind: "handSlotLock" }
  /** Power Grid Outage: zeroes remaining kW on the next Play-phase entry. */
  | { readonly kind: "powerDrain" }
  /**
   * Rodent Infestation: `03-hazards.md` §4's cell restates §3.3's own
   * consequence rather than adding a second one — spec 0007 open question 1,
   * resolved reading (a), carried forward by spec 0033 (owner decision 8) as
   * the slowdown rather than the old routing lock. The card is a plain dead
   * draw; nothing reads this field. `EngineNode.infestation` is the whole
   * mechanic, and stays the only clock.
   */
  | { readonly kind: "infestation" }
  /** Structural Shear: `floor(maxHp * percentOfMaxHp / 100)` HP off every glass edge, once per turn while held. */
  | { readonly kind: "pipeStress"; readonly percentOfMaxHp: number };

/**
 * Why a hazard card was shuffled into the deck (`02-cards-and-economy.md` §2.5: they arrive
 * "when line conditions or maintenance are neglected"). Defined in the
 * protocol, since the event carries it; re-exported here so stage authors
 * writing `pollutionCards` have it to hand.
 */
export type { PollutionReason } from "../protocol/events";

/**
 * Spec 0028: the six Phase 1 facility installs, one answer per
 * `03-hazards.md` §3 consequence — Hazmat Absorbent (§3.1), Surge Protector
 * and Emergency Power Cell (§3.2), Facility Mousetrap (§3.3), HVAC Chillers
 * and Seismic Isolation Mounts (§3.4's two modifiers). Mirrors the
 * `facilityMitigation` zod union `content/src/schema.ts` validates a card's
 * `facility` payload against. A mitigation answers a *consequence*, never the
 * trigger or its announcement (Resolved Question 1) — the engine still fires
 * every `facility.*`/`hazard.cardPenalty` event exactly as before; a
 * mitigation only changes what the numbers do afterward.
 */
export type FacilityMitigation =
  | { readonly kind: "hazmatAbsorbent" }
  | { readonly kind: "surgeProtector" }
  | { readonly kind: "emergencyPower"; readonly kw: number }
  | { readonly kind: "pestControl" }
  | { readonly kind: "hvacChiller" }
  | { readonly kind: "seismicMounts" };

export type FacilityMitigationKind = FacilityMitigation["kind"];

export interface CardDef {
  readonly id: string;
  readonly name: string;
  readonly category: CardCategory;
  readonly costKw: number;
  /** Hardware: deploys this node kind onto an empty tile. */
  readonly deploysNode?: NodeKind;
  /**
   * Hardware: stat overrides for the node it deploys, so two cards can place
   * the same kind of machine at different grades (a plain reactor against a
   * heavy-duty one) without inventing a NodeKind for every variant.
   */
  readonly deploysNodeConfig?: {
    readonly bufferCapacity?: number;
    readonly criticalTemp?: number;
    /**
     * thermalRegulator only (spec 0022): the ±ΔT a card-deployed regulator
     * carries, the same way `criticalTemp` configures a card-deployed
     * reactor's threshold. Omitted means the deployed node's `deltaTemp` is
     * `null` — an unconfigured regulator, same as a stage-authored one with
     * the field left out.
     */
    readonly deltaTemp?: number;
    /**
     * flowMover only (spec 0024): `+1` buffer depth this mover may push past
     * downstream (§4.7). Omitted means `0` — no override, the behaviour every
     * mover had before this spec.
     */
    readonly pumpOverride?: number;
    /**
     * Spec 0031: copied onto the deployed node's `recipeIds` — the deploying
     * card carries the binding (owner, 2026-09-26), so a pathway's card
     * package brings "its" reactor. Omitted means the deployed node selects
     * by kind alone, today's behaviour.
     */
    readonly recipeIds?: readonly string[];
  };
  /** Hardware: places a pipe of this material between two named nodes. */
  readonly deploysPipe?: PipeMaterial;
  readonly attachment?: AttachmentEffect;
  /**
   * Attachment cards only (spec 0023). Restricts which `NodeKind` this may be
   * fitted to — omitted means every node kind accepts it, true of every
   * attachment before the Lid, which fits only a `wasteBuyer`
   * (`applyAttachment`, `engine/cards.ts`, refuses `"wrongNodeKind"` otherwise).
   */
  readonly attachesToNodeKind?: NodeKind;
  readonly directive?: DirectiveEffect;
  readonly hazard?: HazardEffect;
  /**
   * Spec 0028. `metaUtility` only, and mutually exclusive with `directive`
   * (`cardSchema`'s refinement) — the two existing metaUtility cards
   * (Overtime Shift, Rush Order, spec 0020) keep their `directive` payload;
   * the six facility mitigations this spec adds carry this one instead.
   * Bought in Phase 1 through `setupCatalogue` like hardware, but installs
   * onto `EngineState.installedFacilityMitigations` rather than a node or
   * pipe — it occupies no tile.
   */
  readonly facility?: FacilityMitigation;
  /**
   * Bound Hardware (spec 0020, `02-cards-and-economy.md` §2.1): the
   * operational cards installing this device injects into the deck, keyed by
   * catalogue id with a count. `hardware` cards only — a card that deploys
   * nothing has nothing to inject on behalf of. Injected instances carry
   * `boundNodeId` set to the node (or, for a pipe, `null` — pipes inject
   * nothing per the owner's injection table); dismantling that hardware
   * purges every instance it bound from draw pile, hand and discard.
   */
  readonly injectsCards?: Readonly<Record<string, number>>;
  /**
   * Hazard cards only (spec 0007). `false` ⇒ `playCard` refuses it with
   * "this card cannot be played" before any cost is taken
   * (`02-cards-and-economy.md` §2.2's Refused
   * Plays Cost Zero). Omitted (or `true`) means playable, the default for
   * every non-hazard card.
   */
  readonly isPlayable?: boolean;
  /**
   * Hazard cards only (spec 0007). Playing it *exiles* the card — removed
   * from hand and pushed to neither the discard nor draw pile — rather than
   * the ordinary discard every other play takes. This is §4's "until played
   * and cleared" (Clogged Line, Chemical Hazard Spill): the card cannot
   * simply cycle back next draw the way a played hazard could before this
   * spec.
   */
  readonly clearsOnPlay?: boolean;
}

export interface PrecursorDef {
  readonly tier: PrecursorTier;
  /**
   * `budget` only, required there: impurity species added to every emitted
   * packet. Authored per tank rather than fixed in the engine, so the engine
   * contributes no impurity number of its own.
   */
  readonly impurities?: Composition;
}

export interface NodeDef {
  readonly id: string;
  readonly kind: NodeKind;
  readonly pos: GridPos;
  readonly rotation?: Rotation;
  readonly bufferCapacity?: number;
  readonly criticalTemp?: number;
  /** inputTank only: the composition emitted each tick. */
  readonly emits?: Composition;
  /**
   * inputTank only (spec 0027, replaces `precursor`): the two supply
   * contracts a player chooses between in Setup via `setPrecursor`. Omitted
   * means no choice to make — the tank is `{ tier: "premium" }`, free and
   * uncapped, today's behaviour.
   */
  readonly sourcing?: SourcingDef;
  /**
   * thermalRegulator only: integer °C applied to every packet in the node's
   * buffer each tick. Negative chills. A machine property, so it lives on the
   * node like `criticalTemp` — unlike `boilingPoint` and `holdDuration`, which
   * describe a stream and therefore live on the recipe (spec 0003).
   */
  readonly deltaTemp?: number;
  /** inputTank only: emission temperature in integer °C. Defaults to AMBIENT_TEMP_C. */
  readonly feedTemperature?: number;
  /**
   * flowMover only (spec 0024): `+1` buffer depth this mover may push past
   * downstream (§4.7), mirroring `CardDef.deploysNodeConfig.pumpOverride`.
   * Omitted means `0`.
   */
  readonly pumpOverride?: number;
  /**
   * Spec 0031: when present, this node runs ONLY these recipes (still
   * filtered by `RecipeDef.nodeKinds`), in stable id order — see
   * `recipesForNode` (engine/state.ts), the one resolver every kind-only
   * recipe selection routes through. Omitted means every recipe whose
   * `nodeKinds` include this kind, today's behaviour. An unbound node still
   * sees every kind-matching recipe, including one bound to another node;
   * exclusivity is the author's job. An id naming no recipe is inert for it,
   * never a throw — `@reagent/content`'s `validateStage` catches that at
   * authoring time.
   */
  readonly recipeIds?: readonly string[];
}

export interface EdgeDef {
  readonly id: string;
  readonly fromNodeId: string;
  readonly toNodeId: string;
  readonly path: readonly GridPos[];
  readonly material?: PipeMaterial;
  /** phaseConverter outputs only: which fraction this edge carries (spec 0003). */
  readonly phaseRole?: "vapour" | "liquid";
  /** separator outputs only: which fraction this edge carries (spec 0023), mirrors `phaseRole`. */
  readonly separationRole?: "main" | "waste";
}

export interface StageDef {
  readonly id: string;
  /** Shown in the stage picker. */
  readonly name: string;
  /** One line on what this stage is teaching. */
  readonly blurb: string;
  readonly cols: number;
  readonly rows: number;
  readonly nodes: readonly NodeDef[];
  readonly edges: readonly EdgeDef[];
  readonly recipes: readonly RecipeDef[];

  readonly objective: {
    readonly targetComposition: Composition;
    readonly minPurity: number;
  };

  readonly energyBudgetKw: number;
  readonly handSize: number;
  /** Ticks executed per Run phase before returning to Status. */
  readonly ticksPerTurn: number;
  /** Quota must be met within this many turns or the run fails. */
  readonly maxTurns: number;

  /** Species that eat standard pipe walls (`03-hazards.md` §2.3). */
  readonly corrosiveSpecies: readonly string[];

  /**
   * The instances making up the starting draw pile (spec 0016). A standalone
   * stage authors these as static, deterministic instances via
   * `freshCardInstances` below; a run instead composes this from
   * `RunState.deck`'s real, persisted instances (`@reagent/meta`'s
   * `composeStage`) so purchase age, contamination and hardware binding
   * survive the stage boundary untouched — the engine never re-mints an
   * instance it was handed.
   */
  readonly startingDeck: readonly CardInstance[];

  /**
   * Which hazard card each kind of neglect shuffles into the deck
   * (`02-cards-and-economy.md` §2.5). Omit a reason to have that neglect cost
   * nothing but the
   * damage it already did.
   */
  readonly pollutionCards?: Partial<Record<PollutionReason, string>>;

  /**
   * 03-hazards.md §3.2/§3.4 (spec 0006): externally authored facility events,
   * fired once each by turn index. Optional and additive — omitting it (every
   * stage shipped so far) leaves this spec's whole code path a no-op.
   */
  readonly scriptedEvents?: readonly ScriptedEventDef[];

  /**
   * Phase 1's own budget and shop (`00-pillars.md` §3, spec 0017). Optional
   * and additive, like `scriptedEvents` above: omitted means `0` and `[]`,
   * which is every stage this package ships today (`0037` rebalances them).
   * A standalone stage authors a real catalogue directly; a run instead
   * composes `setupBudgetCredits` from `RunState.credits` through
   * `@reagent/meta`'s `composeStage`, the same seam `energyBudgetKw` uses.
   */
  readonly setupBudgetCredits?: number;
  readonly setupCatalogue?: readonly SetupOffer[];

  /**
   * What a dismantled unit sells back for (spec 0021). Composed in by
   * `@reagent/meta`, which owns the ladder's numbers; the engine only applies
   * them. Omitted — every standalone stage — means no resale rule at all: a
   * dismantle is a plain same-setup undo at the full price paid, as before.
   */
  readonly resaleRule?: ResaleRule;

  /**
   * Spec 0067: opens install, dismantle, move and clean during Play. Omitted
   * means Play-phase building is closed, as it was before the spec.
   */
  readonly liveBuild?: LiveBuildRule;
}

/**
 * `02-cards-and-economy.md` §3's depreciation ladder as data. `ladderPct[age]`
 * is the refund for a unit `age` completed stages old, and the last rung
 * holds for every age past it. A contaminated unit refunds nothing and costs
 * `disposalFeePct` of its price instead.
 */
export interface ResaleRule {
  readonly ladderPct: readonly number[];
  readonly disposalFeePct: number;
}

/**
 * The one piece of resale arithmetic, shared by the engine's dismantle event
 * and meta's ledger so the two can never disagree (spec 0021 criterion 6).
 * Signed: a refund is positive, a disposal fee negative. Integer percentages
 * floored, never a float multiply, so a refund never pays more than its rung
 * states and no buy/sell cycle can print Credits (criterion 8).
 */
export function resaleRefund(
  costCredits: number,
  ageStages: number,
  isContaminated: boolean,
  rule: ResaleRule | undefined,
): number {
  if (!rule) return costCredits;
  // `0 -` rather than a unary minus: a fee that floors to nothing must be 0, not -0.
  if (isContaminated) return 0 - Math.floor((costCredits * rule.disposalFeePct) / 100);
  const rung = rule.ladderPct[Math.min(ageStages, rule.ladderPct.length - 1)] ?? 0;
  return Math.floor((costCredits * rung) / 100);
}

/**
 * `CardInstance` lives in `protocol/snapshot.ts` (it is part of `GridSnapshot`
 * now); re-exported from here so stage and card authors keep importing it
 * from this module, the same reasoning as `RecipeDef`/`AttachmentEffect` above.
 */
export type { CardInstance } from "../protocol/snapshot";

/**
 * Mints fresh `CardInstance`s for a static, author-time deck list — every
 * stage's own `StageDef.startingDeck` and `@reagent/meta`'s initial run deck
 * both start here rather than hand-writing five fields per card.
 *
 * Deterministic and side-effect-free: `instanceId` is derived purely from the
 * card's position in the list, so the same list always mints the same ids
 * (`.claude/rules/sim-engine.md` — no `Math.random`, no timestamp, no UUID).
 * The `d` prefix ("deck") keeps these ids out of the way of the engine's own
 * runtime counter (`EngineState.nextInstanceSeq`, prefix `h` for cards it
 * mints mid-stage) and of `@reagent/meta`'s own counter (prefix `r`) — three
 * disjoint namespaces that can never collide once mixed into one hand.
 */
export function freshCardInstances(
  cardIds: readonly string[],
  acquiredStageIndex = 0,
): readonly CardInstance[] {
  return cardIds.map((cardId, i) => ({
    instanceId: `d${i}`,
    cardId,
    acquiredStageIndex,
    boundNodeId: null,
    isContaminated: false,
  }));
}

/** Defaults applied where a NodeDef leaves a field out. */
export const NODE_DEFAULTS = {
  bufferCapacity: 3,
  criticalTemp: 4,
  rotation: 0 as Rotation,
} as const;

export const PIPE_MAX_HP = 100;

/** `03-hazards.md` §2.3: the per-tick HP a corrosive reagent costs a standard pipe. */
export const CORROSION_HP_PER_TICK = 10;

/** `03-hazards.md` §2.2: a pipe bursts once its pressure counter reaches this. */
export const PRESSURE_BURST_THRESHOLD = 3;

/** `03-hazards.md` §2.1: an overheating node explodes after this many uncooled ticks. */
export const OVERHEAT_TICKS_TO_EXPLOSION = 2;

/** `03-hazards.md` §2.5: a packet sitting this long in a buffer turns to sludge. */
export const SLUDGE_RESIDENCE_TICKS = 3;

/** `03-hazards.md` §2.4: purity below this poisons a catalyst bed. */
export const CATALYST_MIN_PURITY = 95;

/** `03-hazards.md` §2.4: the health each impure packet costs the bed. */
export const CATALYST_POISON_PER_IMPURE = 25;

/** 03-hazards.md §3.1: a contaminated tile eats adjacent unlined pipe wall. */
export const CONTAMINATION_HP_PER_TICK = 5;
/** 03-hazards.md §3.3: stagnant sludge this many ticks raises verminRisk. */
export const VERMIN_STAGNANT_TICKS = 5;
/** 03-hazards.md §3.3: rodents arrive at this verminRisk. */
export const VERMIN_RISK_THRESHOLD = 3;
/** 03-hazards.md §3.3 (spec 0033, owner decision 1): an infested node loses 1 tick in 2. */
export const VERMIN_LOST_TICK_PERIOD = 2;
/** 03-hazards.md §3.3 (spec 0033, owner decision 6): 1 tick in 4 under Facility Mousetrap. */
export const VERMIN_LOST_TICK_PERIOD_MOUSETRAP = 4;
